import {
    Box,
    Button,
    formatTimestamp,
    IconButton,
    LogoLoader,
    Report,
    StatusCard,
    StatusCardVariant,
    TextBlock,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    Upload,
} from "@bcc-ui/ui-components";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import style from "./asset-side-panel.scss";
import { UploadCustomReportDialog } from "./UploadCustomReportDialog";
import FileSharingErrorDialog from "components/file-sharing/FileSharingErrorDialog";
import {
    AssetErasureHistory,
    CANCELLED_ERASURE,
    DIAGNOSED_ERASURE,
    FAILED_ERASURE,
    SUCCESSFUL_ERASURE,
    UNKNOWN_ERASURE,
} from "domain/asset-management/assets";
import { assetService } from "services/asset-management/AssetService";
import { fileSharingService } from "services/file-sharing/FileSharingService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    assetUuid: string;
    setToViewReport: (toView: boolean) => void;
    setReportUuid: (uuid: string) => void;
    setReportDate: (reportDate: string) => void;
    assetIdentifier: string;
}
function AssetErasureHistoryView(props: Props): JSX.Element {
    const { t } = useTranslation();

    const [loading, setLoading] = React.useState(true);
    const [uploading, setUploading] = React.useState(false);
    const [events, setEvents] = React.useState<AssetErasureHistory[]>([]);
    const [error, setError] = React.useState<string>();
    const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
    const [downloadError, setDownloadError] = React.useState<string | undefined>();
    const [refreshCounter, setRefreshCounter] = React.useState(0);

    async function handleViewReport(event: AssetErasureHistory) {
        if (event.source === "custom") {
            try {
                const { presigned_url } = await fileSharingService.getDownloadUrl(event.reportUuid);
                window.open(presigned_url, "_blank");
            } catch (error) {
                logger.error("Failed to fetch download URL for custom report.", error);
            }
        } else {
            props.setToViewReport(true);
            props.setReportUuid(event.reportUuid);
            props.setReportDate(event.erasureDate);
        }
    }

    useEffect(() => {
        const abortController = new AbortController();
        assetService
            .fetchAssetErasureHistory(props.assetUuid, abortController)
            .then((response) => {
                if (!abortController.signal.aborted) {
                    setEvents(response);
                }
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Error occurred while fetching asset erasure events.", error);
                    setError(t("AssetManagement.sidePanel.erasureHistory.errorMessage"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            });
        return () => abortController.abort();
    }, [props.assetUuid, refreshCounter]);

    function generatePrimaryContent(event: AssetErasureHistory) {
        let stateKey: string;

        switch (event.erasureStatus) {
            case SUCCESSFUL_ERASURE:
                stateKey = t("Common.state.successful");
                break;
            case FAILED_ERASURE:
                stateKey = t("Common.state.failed");
                break;
            case CANCELLED_ERASURE:
                stateKey = t("Common.state.cancelled");
                break;
            case DIAGNOSED_ERASURE:
                stateKey = t("Common.state.diagnosed");
                break;
            case UNKNOWN_ERASURE:
                stateKey = t("Common.state.unknown");
                break;
            default:
                stateKey = event.erasureStatus;
        }

        return t("AssetManagement.sidePanel.erasureHistory.primaryContent", {
            state: stateKey,
            erasureDate: formatTimestamp(event.erasureDate),
        });
    }

    function generateSecondaryContent(reportUuid: string) {
        return t("AssetManagement.sidePanel.erasureHistory.secondaryContent", { reportUuid: reportUuid });
    }

    function isErasureSuccessful(erasureStatus: string): boolean {
        return erasureStatus === SUCCESSFUL_ERASURE;
    }

    function isErasureFailed(erasureStatus: string): boolean {
        return erasureStatus === FAILED_ERASURE;
    }

    return (
        <>
            {error && <Box>{error}</Box>}
            {uploading || loading ? (
                <Box horizontalAlignment="Center" verticalAlignment="Center" className={style.loadingState}>
                    <LogoLoader />
                </Box>
            ) : (
                <div data-testid={testIds.workArea.asset.sidePanel.erasureTab.itself}>
                    <Box horizontalAlignment={"Right"} marginBottom="Xs">
                        <Button
                            variant={"Secondary"}
                            icon={<Upload />}
                            small={true}
                            onClick={() => setUploadDialogOpen(true)}
                        >
                            {t("AssetManagement.sidePanel.erasureHistory.uploadReport")}
                        </Button>
                    </Box>
                    {events.length < 1 ? (
                        <TextBlock>{t("AssetManagement.sidePanel.erasureHistory.noErasures")}</TextBlock>
                    ) : (
                        events.map((each, index) => (
                            <StatusCard
                                key={index}
                                testIds={{ statusCardTestId: testIds.workArea.asset.sidePanel.erasureTab.statusCard }}
                                primaryText={generatePrimaryContent(each)}
                                secondaryText={generateSecondaryContent(each.reportUuid)}
                                variant={
                                    isErasureSuccessful(each.erasureStatus)
                                        ? StatusCardVariant.SUCCESS
                                        : isErasureFailed(each.erasureStatus)
                                          ? StatusCardVariant.ERROR
                                          : StatusCardVariant.WARNING
                                }
                                actions={
                                    <Tooltip placement={"bottom"}>
                                        <TooltipTrigger>
                                            <IconButton
                                                testId={testIds.workArea.asset.sidePanel.erasureTab.viewReportButton}
                                                onClick={() => void handleViewReport(each)}
                                            >
                                                <Report />
                                            </IconButton>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {each.source === "custom"
                                                ? t("AssetManagement.sidePanel.erasureHistory.downloadReport")
                                                : t("AssetManagement.sidePanel.erasureHistory.viewReport")}
                                        </TooltipContent>
                                    </Tooltip>
                                }
                            />
                        ))
                    )}
                    {downloadError && (
                        <FileSharingErrorDialog
                            error={downloadError}
                            headingText={t("FileSharing.download.errorTitle")}
                            onClose={() => setDownloadError(undefined)}
                        />
                    )}
                </div>
            )}
            <UploadCustomReportDialog
                isOpen={uploadDialogOpen}
                assetUuid={props.assetUuid}
                onClose={() => setUploadDialogOpen(false)}
                assetIdentifier={props.assetIdentifier}
                onUploadComplete={() => {
                    setUploadDialogOpen(false);
                    setRefreshCounter((prev) => prev + 1);
                }}
                setUploading={setUploading}
                uploading={uploading}
            />
        </>
    );
}
export default AssetErasureHistoryView;
